import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import PlatformConditional from '../../../components/PlatformConditional';
import { isPartialDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import { mocaTotalScore, mmseTotalScore, sortAndTakeLatest, getCdrClass, getFtldCdrClass } from '../utils/index';
import { Container, Item } from 'Components/Grid';
import TileContentMaker from 'Components/DashboardTile/TileContentMaker';
import { sortDocuments } from '../../../utility/randomUtil';
import { difference, uniqBy } from 'ramda';
import { calculateCeradTotal } from '../utils/cerad';
import { TDocument } from '../Document/definitions';
import { take } from 'Utility/ramdaReplacement';
import colors from '../../../config/theme/colors';

const fm = (id: string, values?: { [key: string]: string }): React.JSX.Element => (
  <FormattedMessage id={id} values={values || {}} />
);

const getMocaValue = (doc: IMOCA): number | React.JSX.Element | string => {
  const value = (
    <Item xs={true} style={{ textAlign: 'right' }}>
      {doc.unableToPerform ? (
        <FormattedMessage id="cognition.tmt.unableToPerformShort" />
      ) : doc.totalScoreOnly?.[0] === 'totalScoreOnly' ? (
        doc.totalScore
      ) : (
        mocaTotalScore(doc)
      )}
    </Item>
  );
  return value ?? '-';
};

const getMmseValue = (doc: IMMSE): number | string | React.JSX.Element => {
  const value = (
    <Item xs={true} style={{ textAlign: 'right' }}>
      {doc.unableToPerform ? (
        <FormattedMessage id="cognition.tmt.unableToPerformShort" />
      ) : doc.totalScoreOnly?.[0] === 'totalScoreOnly' ? (
        doc.totalScore
      ) : (
        mmseTotalScore(doc)
      )}
    </Item>
  );
  return value;
};

const getTmtValue = (doc: ITMT, part: 'a' | 'b'): React.JSX.Element | undefined => {
  if (part === 'a' && !(doc.tmtaUnableToPerform || doc.tmtaTime)) return undefined;
  if (part === 'b' && !(doc.tmtbUnableToPerform || doc.tmtbTime)) return undefined;
  const valueOfLatest = (
    <React.Fragment>
      <Container>
        <Item xs={true} style={{ textAlign: 'right' }}>
          {doc[part === 'a' ? 'tmtaUnableToPerform' : 'tmtbUnableToPerform']?.[0] === 'unableToPerform' ? (
            <FormattedMessage id="cognition.tmt.unableToPerformShort" />
          ) : doc[part === 'a' ? 'tmtaTime' : 'tmtbTime'] || doc[part === 'a' ? 'tmtaTime' : 'tmtbTime'] === 0 ? (
            `${doc[part === 'a' ? 'tmtaTime' : 'tmtbTime']} s`
          ) : (
            '-'
          )}
        </Item>
      </Container>
    </React.Fragment>
  );
  return valueOfLatest;
};

const getCdrValue = (doc: ICDR): React.JSX.Element => {
  const cdrClass = getCdrClass(doc);
  const ftldCdrClass = getFtldCdrClass(doc);
  return (
    <>
      <div>{cdrClass ?? '-'}</div>
      <div>{ftldCdrClass ?? '-'}</div>
    </>
  );
};

const getSdmtValue = (doc: ISDMT): React.JSX.Element => {
  const valueOfLatest = (
    <React.Fragment>
      <Container>
        <Item xs={true} style={{ textAlign: 'right' }}>
          {doc.correctAnswers ?? '-'}
          {' / '}
          {doc.allAnswers ?? '-'}
        </Item>
      </Container>
    </React.Fragment>
  );
  return valueOfLatest;
};

const getCeradValue = (doc: ICERAD2023): number | string | React.JSX.Element => {
  const value = (
    <Item xs={true} style={{ textAlign: 'right' }}>
      {calculateCeradTotal(doc) ?? '-'}
    </Item>
  );
  return value;
};

const latestDocumentsByType = (documents: IOwnProps['documents']) => {
  const sortedDocs = documents.sort((n1, n2) => sortDocuments([{ type: 'date', sortField: 'date' }])(n1, n2));
  const uniqDocs = uniqBy(d => d._type, sortedDocs);
  return uniqDocs;
};

const parkinsonTypesDisplayed = (type: string) =>
  ({
    moca: 'MOCA',
    mmse: 'MMSE',
    tmt: 'TMT',
    cdr: 'CDR & FTLD-CDR',
    sdmt: 'SDMT',
    cerad: 'CERAD',
  })[type];

const CognitionDash = ({ documents }: IOwnProps): React.JSX.Element => {
  const docs = latestDocumentsByType(documents);

  const parkinsonDocTypes = ['moca', 'mmse', 'tmt', 'cdr', 'sdmt', 'cerad'];
  const parkinsonIncludedTypes = take(
    5,
    docs.filter(doc => parkinsonDocTypes.includes(doc._type)),
  ).map(d => d._type);

  let canfitParkinsonItemsAmount = 5;
  // If the newest parkinson meters include both TMT and CDR, then the tile can (potentially) only fit 4 recent values and only fit
  if (parkinsonIncludedTypes.includes('tmt') && parkinsonIncludedTypes.includes('cdr')) canfitParkinsonItemsAmount = 4;

  const parkinsonMissingTypes = difference(
    parkinsonDocTypes,
    take(
      canfitParkinsonItemsAmount,
      docs.filter(doc => parkinsonDocTypes.includes(doc._type)),
    ).map(d => d._type),
  );

  return (
    <React.Fragment>
      <PlatformConditional platform={['parkinson']}>
        <TileContentMaker
          type="specificList"
          itemsCanFitAmount={canfitParkinsonItemsAmount}
          data={docs
            .filter(doc => parkinsonDocTypes.includes(doc._type))
            .map(d => {
              switch (d._type) {
                case 'moca':
                  return {
                    date: isPartialDate(d.date) ? formatPartialDate(d.date) : '',
                    title: fm('cognition.moca.moca'),
                    value: getMocaValue(d as IMOCA),
                    key: 'moca',
                  };

                case 'mmse':
                  return {
                    date: isPartialDate(d.date) ? formatPartialDate(d.date) : '',
                    title: fm('cognition.mmse.mmse'),
                    value: getMmseValue(d as IMMSE),
                    key: 'mmse',
                  };

                case 'tmt':
                  return {
                    date: isPartialDate(d.date) ? formatPartialDate(d.date) : '',
                    title: (
                      <>
                        {getTmtValue(d as ITMT, 'a') && <div>{fm('cognition.tmt.tmta')}</div>}
                        {getTmtValue(d as ITMT, 'b') && <div>{fm('cognition.tmt.tmtb')}</div>}
                      </>
                    ),
                    value: (
                      <>
                        {getTmtValue(d as ITMT, 'a') && <div>{getTmtValue(d as ITMT, 'a')}</div>}
                        {getTmtValue(d as ITMT, 'b') && <div>{getTmtValue(d as ITMT, 'b')}</div>}
                      </>
                    ),
                    key: 'tmtCombined',
                  };

                case 'cdr':
                  return {
                    date: isPartialDate(d.date) ? formatPartialDate(d.date) : '',
                    title: (
                      <>
                        <div>{fm('cognition.cdr.titleShort')}</div>
                        <div>{fm('cognition.cdr.ftldShort')}</div>
                      </>
                    ),
                    value: getCdrValue(d as ICDR),
                    key: 'cdr',
                  };

                case 'sdmt':
                  return {
                    date: isPartialDate(d.date) ? formatPartialDate(d.date) : '',
                    title: fm('cognition.sdmt.sdmt'),
                    value: getSdmtValue(d as ISDMT),
                    key: 'sdmt',
                  };

                case 'cerad':
                  return {
                    date: isPartialDate(d.date) ? formatPartialDate(d.date) : '',
                    title: fm('cognition.cerad.cerad'),
                    value: getCeradValue(d as ICERAD2023),
                    key: 'cerad',
                  };

                default:
                  return {
                    date: isPartialDate(d.date) ? formatPartialDate(d.date) : '',
                    title: fm('cognition.default'),
                    key: 'default',
                  };
              }
            })}
        />
        {parkinsonMissingTypes.length > 0 ? (
          <div style={{ color: colors.darkGray, fontSize: '1.4rem', marginTop: '0.2rem' }}>
            <FormattedMessage
              id={parkinsonMissingTypes.length === 6 ? 'general.theseMeters' : 'general.alsoTheseMeters'}
            />
            : {parkinsonMissingTypes.map(type => parkinsonTypesDisplayed(type)).join(', ')}
          </div>
        ) : (
          <></>
        )}
      </PlatformConditional>
      <PlatformConditional platform={'ms'}>
        <TileContentMaker
          type="recentList"
          data={(sortAndTakeLatest(['sdmt'], documents) as ISDMT[]).slice(0, 6).map(d => ({
            date: isPartialDate(d.date) ? formatPartialDate(d.date) : '',
            title: fm('cognition.sdmt.sdmt'),
            value: getSdmtValue(d),
            key: d._id,
          }))}
        />
      </PlatformConditional>
      <PlatformConditional platform={'huntington'}>
        <TileContentMaker
          type="recentList"
          data={sortAndTakeLatest(['sdmt'], documents).map(s => ({
            date: isPartialDate(s.date) ? formatPartialDate(s.date) : '',
            title: fm('cognition.sdmt.sdmt'),
            value: getSdmtValue(s as ISDMT),
            key: s._id,
          }))}
        />
      </PlatformConditional>
    </React.Fragment>
  );
};

interface IOwnProps {
  documents: Array<TDocument>;
}

export default CognitionDash;
